import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Fab, 
  IconButton,
  Avatar,
  List,
  ListItem,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { Send as SendIcon, Message, Close as CloseIcon } from '@mui/icons-material';
import { FaMicrophone } from 'react-icons/fa';
import { getAuthToken } from 'src/utils/auth';
import { useSpeechToText } from '../Speech/SpeechToText';
import splitStringByJson from 'src/utils/splitString';
import { v4 as uuid } from 'uuid';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#10a37f',
    },
    background: {
      default: '#ffffff',
      paper: '#f7f7f8',
    },
    text: {
      primary: '#374151',
    },
  },
  typography: {
    fontFamily: '"Söhne", "Helvetica Neue", "Arial", sans-serif',
  },
});

interface Message {
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
}

interface Citation {
  generatedResponsePart: {
    textResponsePart: {
      span: {
        start: number;
        end: number;
      };
      text: string;
    };
  };
  retrievedReferences: RetrievedReference[];
}

interface RetrievedReference {
  content: {
    text: string;
  };
  location: {
    s3Location: {
      uri: string;
    };
  };
}

export default function MiniChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'Hello! How can I help you today?' },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string>(uuid());
  const [isListening, setIsListening] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const styles = {
    micButton: {
      padding: '10px 15px',
      color: '#fff',
      borderRadius: '15px',
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (inputMessage.trim() !== '') {
      setMessages(prevMessages => [...prevMessages, { role: 'user', content: inputMessage }]);
      setInputMessage('');
      setIsTyping(true);

      try {
        const controller = new AbortController();
        setAbortController(controller);

        // Immediately add an empty assistant message
        setMessages(prevMessages => [...prevMessages, { role: 'assistant', content: '' }]);
        
        const { idToken } = await getAuthToken();

        const response = await fetch('https://www.mai-backend-service.com/agentChatStream', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            prompt: inputMessage,
            model: 4,
            sessionId: sessionId
          }),
          signal: controller.signal,
        });

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';
        let agent_citation = '';
        let agent_citation_json = { citation: {} as Citation };
        let citation_list: Citation[] = [];

        while (true) {
          const { done, value } = await reader?.read() || {};
          if (done) break;

          const chunk = decoder.decode(value);
          let [beforeJson, jsonPart, afterJson, jsonChunk] = splitStringByJson(chunk);

          try {
            if (jsonChunk) {
              if (jsonChunk === "firstHalfJson") {
                agent_citation += jsonPart;
                continue;
              }
              if (jsonChunk === "secondHalfJson" || jsonChunk === "completeJson") {
                agent_citation += jsonPart;
              }

              agent_citation_json = JSON.parse(agent_citation);
              if (agent_citation_json.citation) {
                citation_list.push(agent_citation_json.citation);
              }
              agent_citation = '';
            }
          } catch (error) {
            console.log("error parsing in chunk ", error);
          }

          if (afterJson) {
            fullResponse += afterJson;
          } else if (beforeJson) {
            fullResponse += beforeJson;
          }

          setMessages(prevMessages => {
            const newMessages = [...prevMessages];
            const lastMessageIndex = newMessages.length - 1;
            
            newMessages[lastMessageIndex] = {
              ...newMessages[lastMessageIndex],
              content: fullResponse.trim()
            };
            return newMessages;
          });
        }

        setMessages(prevMessages => {
          const newMessages = [...prevMessages];
          const lastMessageIndex = newMessages.length - 1;
          
          newMessages[lastMessageIndex] = {
            ...newMessages[lastMessageIndex],
            content: fullResponse.trim(),
            ...(citation_list.length > 0 ? { citations: citation_list } : {})
          };
          return newMessages;
        });

      } catch (error) {
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            console.log("Request Aborted");
          } else {
            console.error('Error fetching bot response:', error);
            setMessages(prevMessages => [...prevMessages, { 
              role: 'assistant', 
              content: `An error occurred: ${error}`
            }]);
          }
        }
      } finally {
        setIsTyping(false);
      }
    }
  };

  // Rest of the component remains the same...
  const handleResult = (transcript: React.SetStateAction<string>) => {
    setInputMessage(transcript);
    setIsListening(false);
  };

  const handleError = (error: any) => {
    setIsListening(false);
  };

  const { startListening, stopListening, isSupported } = useSpeechToText(
    handleResult,
    handleError
  );

  const toggleListening = () => {
    if (!isSupported) return;
    if (isListening) {
      stopListening();
      setIsListening(false);
    } else {
      startListening();
      setIsListening(true);
    }
  };

  const renderMessageContent = (content: string) => {
    const processedContent = content.replace(/\\l/g, '\n');
    return processedContent.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < processedContent.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <ThemeProvider theme={theme}>
      <Fab 
        color="primary" 
        size="small"
        onClick={() => setIsOpen(!isOpen)} 
        sx={{ 
          position: 'fixed', 
          top: 16, 
          right: 70,  
          zIndex: 1200  
        }}
      >
        <Message />
      </Fab>

      {isOpen && (
        <Box 
          sx={{ 
            position: 'fixed', 
            top: 80,  
            right: 16,  
            width: 350,  
            height: 500, 
            border: '1px solid #ddd',
            borderRadius: 8,  
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',  
            backgroundColor: 'white',  
            display: 'flex', 
            flexDirection: 'column',
            zIndex: 1200 
          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider'
            }}
          >
            <Typography variant="h6">Moder AI Assistant</Typography>
            <IconButton onClick={() => setIsOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <List 
            sx={{ 
              flexGrow: 1, 
              overflow: 'auto', 
              p: 2, 
              bgcolor: 'background.paper' 
            }}
          >
            {messages.map((message, index) => (
              <ListItem 
                key={index} 
                sx={{ 
                  display: 'flex', 
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start', 
                  alignItems: 'flex-start', 
                  mb: 2 
                }}
              >
                {message.role === 'assistant' && (
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>AI</Avatar>
                )}
                <Box
                  sx={{
                    maxWidth: '70%',
                    bgcolor: message.role === 'user' ? 'primary.main' : 'background.default',
                    color: message.role === 'user' ? 'common.white' : 'text.primary',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography variant="body1">
                    {renderMessageContent(message.content)}
                    {message.role === 'assistant' && isTyping && index === messages.length - 1 && (
                      <Box 
                        component="span" 
                        sx={{ 
                          display: 'inline-block', 
                          width: 4, 
                          height: 16, 
                          bgcolor: 'primary.main', 
                          ml: 0.5, 
                          animation: 'blink 1s infinite' 
                        }} 
                      />
                    )}
                  </Typography>
                </Box>
              </ListItem>
            ))}
            <div ref={messagesEndRef} />
          </List>

          <Box 
            sx={{ 
              p: 2, 
              borderTop: 1, 
              borderColor: 'divider', 
              bgcolor: 'background.default' 
            }}
          >
            <form 
              onSubmit={(e) => { 
                e.preventDefault(); 
                handleSendMessage(); 
              }}
              onKeyDown={(e) => {
                if(e.key === 'Enter' && !e.shiftKey){
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Type your message here..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  sx={{
                    mr: 1,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      borderRadius: '0.75rem',
                    },
                  }}
                />
                <button
                  type="button"
                  onClick={toggleListening}
                  style={{
                    ...styles.micButton,
                    backgroundColor: isListening ? '#ff4d4d' : '#007bff',
                  }}
                >
                  <FaMicrophone />
                </button>
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<SendIcon />}
                  onClick={handleSendMessage}
                  disabled={isTyping || inputMessage.trim() === ''}
                  sx={{ 
                    borderRadius: '0.75rem', 
                    minWidth: '44px', 
                    height: '44px', 
                    ml: 1 
                  }}
                >
                  Send
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      )}
    </ThemeProvider>
  );
}